import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  TextField 
} from '@mui/material';

interface RenameDialogProps {
  open: boolean;
  title: string;
  label?: string;
  initialValue: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

const RenameDialog: React.FC<RenameDialogProps> = ({
  open,
  title,
  label = 'Name',
  initialValue,
  confirmText = 'Rename',
  cancelText = 'Cancel',
  onConfirm,
  onCancel
}) => {
  const [value, setValue] = useState(initialValue);

  // Update local state when initialValue changes or dialog opens
  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [open, initialValue]);

  const handleConfirm = () => {
    if (value.trim()) {
      onConfirm(value.trim());
      setValue('');
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    onCancel();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 'var(--border-radius-md)',
          padding: 0
        }
      }}
    >
      <DialogTitle sx={{ 
        color: 'var(--neutral-dark)',
        fontFamily: 'inherit',
        fontSize: '18px',
        fontWeight: 'bold',
        padding: 'var(--spacing-l)',
        paddingBottom: 'var(--spacing-m)'
      }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ 
        padding: '0 var(--spacing-l)',
        paddingBottom: 'var(--spacing-m)'
      }}>
        <TextField
          autoFocus
          fullWidth
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={`Enter new ${label.toLowerCase()}`}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 'var(--border-radius-sm)',
              fontFamily: 'inherit',
              fontSize: '14px',
              '& fieldset': {
                borderColor: 'var(--neutral-light)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--primary-color)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--primary-color)',
                borderWidth: '1px',
              },
            },
            '& .MuiInputBase-input': {
              padding: 'var(--spacing-s) var(--spacing-m)',
              color: 'var(--neutral-dark)',
              fontFamily: 'inherit',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'var(--neutral-light)',
              opacity: 1,
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ 
        padding: 'var(--spacing-l)',
        paddingTop: 0,
        gap: 'var(--spacing-m)',
        justifyContent: 'flex-end'
      }}>
        <Button 
          onClick={handleCancel}
          sx={{
            padding: 'var(--spacing-s) var(--spacing-m)',
            borderRadius: 'var(--border-radius-sm)',
            fontFamily: 'inherit',
            fontSize: '14px',
            textTransform: 'none',
            color: 'var(--neutral-dark)',
            border: '1px solid var(--neutral-light)',
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: '#F5F5F5'
            }
          }}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          disabled={!value.trim()}
          sx={{
            padding: 'var(--spacing-s) var(--spacing-m)',
            borderRadius: 'var(--border-radius-sm)',
            fontFamily: 'inherit',
            fontSize: '14px',
            textTransform: 'none',
            backgroundColor: 'var(--primary-color)',
            '&:hover': {
              backgroundColor: '#277a64'
            },
            '&.Mui-disabled': {
              backgroundColor: 'var(--neutral-light)',
              color: 'white'
            }
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameDialog; 